// World screen — persistent chrome (quest bar, activity panel, bag card,
// notification pane + info pane in the right column) around an icon-tab rail
// of mutually exclusive views: location | crafting | stats | inventory |
// class | map | codex. Everything renders from the vsj; all interaction funnels
// through the driver. Forced modals: intro → offline → quest-claim, with the
// claim→next-quest chain held under one scrim.

import { clear, el, flashClass, kbar, kbtn, tooltip } from '../kit';
import { sendEvent } from '../net';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';
import * as fx from '../fx';
import * as sfx from '../audio/sfx';
import * as music from '../audio/music';
import { gainsStrip } from '../components/gains';
import { questBar } from '../components/quest_bar';
import { activityPanel } from '../components/activity_panel';
import { infoPane } from '../components/info_pane';
import { bindGrid } from '../components/slots';
import { openQuestLog } from '../components/quest_log';
import { openSettings } from '../components/settings';
import { locationView } from '../views/location';
import { craftingView } from '../views/crafting';
import { skillsView } from '../views/skills';
import { classView } from '../views/class';
import { inventoryView } from '../views/inventory';
import { mapView } from '../views/map';
import { codexView } from '../views/codex';

const TABS = [
  { id: 'location', icon: 'glyph_stand', reveal: 'inventory', title: 'Location' },
  { id: 'crafting', icon: 'tab_crafting', reveal: 'crafting', title: 'Crafting' },
  { id: 'stats', icon: 'tab_skills', reveal: 'skills', title: 'Stats' },
  { id: 'inventory', icon: 'tab_inventory', reveal: 'inventory', title: 'Inventory' },
  { id: 'class', icon: 'tab_class', reveal: 'class', title: 'Class' },
  { id: 'map', icon: '_map_grid', reveal: 'map', title: 'Map' },
  { id: 'codex', icon: 'glyph_creature', reveal: 'inventory', title: 'Codex' },
];

interface View {
  root: HTMLElement;
  refresh(changed: Set<string>): void;
  onShown?(): void;
}

export function worldScreen(): { root: HTMLElement; onVsj(changed: Set<string>): void } {
  // ---- views ----
  const views: Record<string, View> = {
    location: locationView(),
    crafting: craftingView(),
    stats: skillsView(),
    inventory: inventoryView(),
    class: classView(),
    map: mapView(),
    codex: codexView(),
  };
  let currentView = '';

  // ---- tab rail ----
  const rail = el('div', { class: 'tab-rail' });
  const tabButtons: Record<string, HTMLButtonElement> = {};
  for (const tab of TABS) {
    const glyph = tab.icon === '_map_grid' ? icons.mapTabGlyph(24) : icons.texture(tab.icon, 24);
    const b = el('button', { class: 'k-btn' }, glyph, el('span', { text: tab.title }));
    // the location tab wears a small combat glyph while a fight is live
    // (shown by the same .combat class that tints the tab)
    if (tab.id === 'location')
      b.append(el('span', { class: 'combat-badge' }, icons.texture('tab_combat', 14)));
    b.addEventListener('click', () => {
      sfx.playEvent('ui_tab');
      switchView(tab.id);
    });
    rail.append(b);
    tabButtons[tab.id] = b;
  }
  rail.append(el('div', { class: 'spacer' }));
  const settingsBtn = el('button', { class: 'k-btn' }, el('span', { style: 'font-size:20px', text: '⚙' }), el('span', { text: 'Settings' }));
  settingsBtn.addEventListener('click', () => {
    sfx.playEvent('ui_tick');
    openSettings(true);
  });
  const switchBtn = el('button', { class: 'k-btn' }, icons.texture('switch_character', 24), el('span', { text: 'Switch' }));
  switchBtn.addEventListener('click', () => {
    sfx.playEvent('ui_tick');
    sendEvent('exit_character');
  });
  rail.append(settingsBtn, switchBtn);

  // ---- chrome pieces ----
  const qbar = questBar(() => openQuestLog(qbar.root));
  const apanel = activityPanel();
  const ipane = infoPane();
  const notif = fx.buildPane();

  const viewHost = el('div', { class: 'view-host' });
  for (const id of Object.keys(views)) {
    views[id].root.style.display = 'none';
    viewHost.append(views[id].root);
  }

  // ---- persistent pool readout (hp / stamina / mana — served values only) ----
  const POOLS: [string, string, string, string, string][] = [
    ['hp', 'ui_hp', 'hp', 'hp_max', 'Hit points'],
    ['stamina', 'ui_stamina', 'stamina', 'stamina_max', 'Stamina'],
    ['mana', 'ui_mana', 'mana', 'mana_max', 'Mana'],
  ];
  const poolCard = el('div', { class: 'k-panel pool-card' });
  const poolBars: { bar: ReturnType<typeof kbar>; num: HTMLElement; cur: string; max: string }[] = [];
  for (const [kind, icon, curKey, maxKey, label] of POOLS) {
    const bar = kbar(kind);
    const num = el('span', { class: 'pool-num' });
    const row = el('div', { class: 'pool-row' }, icons.texture(icon, 14), bar.root, num);
    tooltip(row, () => label);
    poolCard.append(row);
    poolBars.push({ bar, num, cur: curKey, max: maxKey });
  }
  const refreshPools = () => {
    const ch = vsj.at('character') ?? {};
    for (const p of poolBars) {
      const cur = Math.floor(Number(ch[p.cur] ?? 0));
      const max = Math.floor(Number(ch[p.max] ?? 0));
      p.bar.setHp(cur, max);
      p.num.textContent = `${cur} / ${max}`;
    }
  };

  const bagTitle = el('div', { class: 'k-small k-dim', text: 'Bag' });
  const bagGrid = el('div');
  const bagCard = el('div', { class: 'k-panel bag-card', style: 'flex:none' }, bagTitle, bagGrid);
  let bagSig = '';

  const root = el(
    'div',
    { class: 'world' },
    el(
      'div',
      { class: 'world-main' },
      el(
        'div',
        { class: 'world-left' },
        rail,
        el(
          'div',
          { class: 'world-left-col' },
          qbar.root,
          viewHost,
          el('div', { class: 'world-bottom' }, apanel.root, poolCard, el('div', { style: 'flex:1' }), bagCard),
        ),
      ),
      el('div', { class: 'world-right' }, notif, ipane.root),
    ),
  );

  // ---- tabs / reveal ----
  // per-tab visibility is a SERVED verdict (vsj.tabs) — the reveal->tab
  // mapping lives on the driver
  const tabVisible = (tab: { id: string }): boolean =>
    !!(vsj.at('tabs') ?? {})[tab.id];

  const refreshTabGlows = () => {
    const glow: string[] = vsj.at('guidance.glow') ?? [];
    for (const tab of TABS)
      // the tab you're already ON never glows — something INSIDE carries it
      tabButtons[tab.id].classList.toggle('glow', tab.id !== currentView && glow.includes('tab:' + tab.id));
  };

  const refreshTabs = () => {
    for (const tab of TABS) tabButtons[tab.id].style.display = tabVisible(tab) ? '' : 'none';
    refreshTabGlows();
    if (currentView && tabButtons[currentView].style.display === 'none') showFirstVisibleTab();
  };

  // combat happens on the Location tab — tint it while a fight is live
  const refreshCombatTab = () => tabButtons['location'].classList.toggle('combat', !!vsj.at('fight'));

  const showFirstVisibleTab = () => {
    for (const tab of TABS)
      if (tabVisible(tab)) {
        switchView(tab.id);
        return;
      }
  };

  function switchView(id: string): void {
    currentView = id;
    music.setMapOpen(id === 'map'); // map view is where the soundtrack opens up
    for (const tab of TABS) tabButtons[tab.id].classList.toggle('active', tab.id === id);
    for (const vid of Object.keys(views)) views[vid].root.style.display = vid === id ? 'flex' : 'none';
    // the inventory view already shows the bag — hide the chrome bag card
    bagCard.style.display = id === 'inventory' ? 'none' : '';
    views[id].onShown?.();
    refreshTabGlows();
  }

  ui.onViewRequest((id) => {
    if (tabButtons[id] && tabButtons[id].style.display !== 'none') switchView(id);
  });

  // ---- chrome bag card ----
  const refreshBag = () => {
    const first = vsj.at('bags.0');
    if (!first || typeof first !== 'object') {
      clear(bagGrid);
      bagSig = '';
      return;
    }
    const s = JSON.stringify(first);
    if (s !== bagSig) {
      bagSig = s;
      bindGrid(bagGrid, 'bag0', first.slots ?? {}, { columns: 9 });
    }
    bagTitle.textContent = first.bag != null ? vsj.infoName(String(first.bag)) : 'Bag';
  };

  // ---- forced modals ----
  const modalHolder = el('div');
  root.append(modalHolder);
  let claimHoldUntil = 0;
  let questAckLive = false;

  const modalLive = (): boolean => modalHolder.childElementCount > 0;

  const makeModal = (): HTMLElement => {
    const body = el('div', { class: 'k-modal', style: 'align-items:center;text-align:center' });
    const scrim = el('div', { class: 'k-scrim' }, body);
    modalHolder.append(scrim);
    return body;
  };

  const clearModals = () => clear(modalHolder);

  const refreshModals = () => {
    if (questAckLive) return;
    const welcome = vsj.at('welcome');
    const rep = vsj.at('offline_report');
    // hold the claim scrim while the successor quest popup is still coming
    if (Date.now() < claimHoldUntil && modalLive() && !welcome && !rep) return;
    clearModals();
    if (welcome && typeof welcome === 'object') {
      const body = makeModal();
      body.append(el('div', { class: 'k-title', text: 'Welcome to Stoneheim' }));
      for (const line of [
        "Skills grow as you use them — and your work carries on while you're away.",
        'The quest bar at the top always shows your next step.',
      ])
        body.append(el('div', { class: 'k-dim', text: line }));
      const ok = kbtn(welcome.ack, { label: 'OK' });
      ok.addEventListener('click', () => {
        claimHoldUntil = Date.now() + 800;
        setTimeout(() => refreshModals(), 850);
      });
      body.append(ok);
      return;
    }
    if (rep && typeof rep === 'object') {
      buildOfflineModal(rep);
      return;
    }
    const qb = vsj.at('quest_bar');
    if (qb && typeof qb === 'object' && qb.claim && typeof qb.claim === 'object') buildClaimModal(qb);
  };

  const buildClaimModal = (qb: any) => {
    const body = makeModal();
    body.append(
      el('div', { class: 'k-small', style: 'color:var(--gold)', text: 'Quest complete' }),
      el('div', { class: 'auth-title', text: vsj.infoName(String(qb.tracked ?? '')) }),
    );
    const reward = qb.claim_reward;
    if (reward && typeof reward === 'object') {
      const chips = el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap;justify-content:center' });
      if (Array.isArray(reward.items))
        for (const it of reward.items) {
          if (!it || typeof it !== 'object') continue;
          chips.append(icons.plated(String(it.item ?? ''), 22, 3), el('span', { text: `×${it.count ?? 0}` }));
        }
      if (Array.isArray(reward.xp))
        for (const x of reward.xp) {
          if (!x || typeof x !== 'object') continue;
          const skillName = String(vsj.info('skill_' + x.skill)?.name ?? x.skill ?? '');
          chips.append(el('span', { style: 'color:var(--bar-xp)', text: `+${x.amount ?? 0} ${skillName} xp` }));
        }
      body.append(chips);
    }
    const claim = kbtn(qb.claim, { label: 'Claim reward', cls: 'claim-pulse', clickSound: 'claim' });
    claim.addEventListener('click', () => {
      claimHoldUntil = Date.now() + 800;
      setTimeout(() => refreshModals(), 850); // chain ends: let the hold lapse
    });
    body.append(claim);
  };

  const buildOfflineModal = (rep: any) => {
    const body = makeModal();
    body.style.textAlign = 'left';
    body.style.alignItems = 'stretch';
    body.append(
      el('div', { class: 'k-title', text: 'While you were away' }),
      el('div', { class: 'k-dim', text: String(rep.away_text ?? '') }),
    );
    if (rep.slipped_away_text) body.append(el('div', { style: 'color:var(--warn)', text: String(rep.slipped_away_text) }));
    let stagger = 0;
    const countUp = (node: HTMLElement, fmt: (n: number) => string, target: number, index: number) => {
      const start = performance.now() + index * 110;
      const step = () => {
        const t = Math.max(0, Math.min(1, (performance.now() - start) / 900));
        const eased = 1 - Math.pow(1 - t, 3);
        node.textContent = fmt(Math.round(target * eased));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    // served DISPLAY ROWS — items registered in infos, names/copy all served
    const itemRows: any[] = Array.isArray(rep.item_rows) ? rep.item_rows : [];
    const xpRows: any[] = Array.isArray(rep.xp_rows) ? rep.xp_rows : [];
    if (itemRows.length) {
      body.append(el('div', { class: 'k-small k-faint', text: 'Gained' }));
      for (const r of itemRows) {
        if (!r || typeof r !== 'object') continue;
        const num = el('span', { text: '×0' });
        body.append(
          el(
            'div',
            { style: 'display:flex;gap:6px;align-items:center' },
            icons.plated(String(r.item ?? ''), 22, 3),
            el('span', { style: 'flex:1', text: String(r.name ?? r.item ?? '') }),
            num,
          ),
        );
        countUp(num, (n) => `×${n}`, Number(r.count ?? 0), stagger++);
      }
    }
    if (xpRows.length) {
      body.append(el('div', { class: 'k-small k-faint', text: 'XP' }));
      for (const r of xpRows) {
        if (!r || typeof r !== 'object') continue;
        const row = el(
          'div',
          { style: 'display:flex;gap:6px;align-items:center' },
          el('span', { style: 'flex:1', text: String(r.name ?? r.skill ?? '') }),
        );
        if (Number(r.levels ?? 0) > 0)
          row.append(el('span', { class: 'k-chip', style: 'border-color:var(--gold);color:var(--gold)', text: `Lv +${r.levels}` }));
        if (Number(r.xp ?? 0) > 0) {
          const num = el('span', { style: 'color:var(--bar-xp)', text: '+0' });
          row.append(num);
          countUp(num, (n) => `+${n}`, Number(r.xp), stagger++);
        }
        body.append(row);
      }
    }
    if (rep.empty_text) body.append(el('div', { class: 'k-faint', text: String(rep.empty_text) }));
    // served footnotes — faint lines at the bottom of the report
    if (Array.isArray(rep.footnotes))
      for (const fnote of rep.footnotes)
        body.append(el('div', { class: 'k-small k-faint', text: String(fnote) }));
    body.append(kbtn(rep.collect, { label: 'Collect' }));
  };

  // new quest — forced ack; client-side only, Accept just dismisses
  fx.onFxType('quest_started', (f: any) => {
    claimHoldUntil = 0;
    clearModals();
    questAckLive = true;
    const body = makeModal();
    body.append(
      el('div', { class: 'k-small', style: 'color:var(--gold)', text: 'New quest' }),
      el('div', { class: 'auth-title', text: String(f.name ?? '') }),
      el('div', { class: 'k-dim', text: String(f.firstStep ?? '') }),
    );
    const go = el('button', { class: 'k-btn', text: 'Accept' });
    go.addEventListener('click', () => {
      questAckLive = false;
      clearModals();
      refreshModals();
    });
    body.append(go);
  });

  // ---- fx juice ----
  // gains strip: small icon boxes at the bottom of the screen, fed by fx
  root.append(gainsStrip().root);
  const floater = (text: string, color: string, near: HTMLElement) => {
    if (!near.isConnected) return;
    fx.floatText(near, text, color);
  };
  fx.onFxType('xp_gained', (f: any) => {
    if (modalLive()) return;
    floater(`+${f.amount ?? '?'} ${String(f.skill ?? '')} xp`, 'var(--bar-xp)', qbar.root);
  });
  fx.onFxType('gather_empty', () => floater('nothing…', 'var(--text-faint)', apanel.root));

  fx.onFxType('items_gained', (f: any) => {
    if (modalLive()) return;
    const items = f.items;
    if (!Array.isArray(items)) return;
    let i = 0;
    for (const it of items) {
      if (!it || typeof it !== 'object') continue;
      const delay = 90 * i++;
      const item = String(it.item ?? '');
      const to = String(it.to ?? 'bag');
      const count = Number(it.count ?? 1);
      setTimeout(() => {
        if (to === 'ground') floater(`${vsj.infoName(item)} ×${count} → ground`, 'var(--text-dim)', apanel.root);
        else flyIcon(item, count);
      }, delay);
    }
  });

  // level-up: the notification pane entry (fx.ts) is the whole moment.

  // first discovery: quiet by design — the discovery-gold notification pane
  // entry (fx.ts) is the whole moment; no takeover.

  fx.onFxType('item_broke', (f: any) => {
    flashClass(bagCard, 'ring-flash');
    const item = String(f.item ?? '');
    if (!ui.reducedMotion()) {
      // brief edge vignette — a quiet "something gave way", not an alarm
      const v = el('div', { class: 'fx-vignette' });
      document.body.append(v);
      setTimeout(() => v.remove(), 450);
      // the icon comes apart over the bag; a spare visibly drops in after
      const r = bagCard.getBoundingClientRect();
      const at = `left:${r.left + r.width / 2 - 20}px;top:${r.top + r.height / 2 - 20}px;width:40px;height:40px`;
      const ghost = el('div', { class: 'fx-shatter', style: at }, icons.texture(item, 40));
      document.body.append(ghost);
      setTimeout(() => ghost.remove(), 500);
      if (f.replaced)
        setTimeout(() => {
          const sw = el('div', { class: 'fx-swapin', style: at }, icons.texture(item, 40));
          document.body.append(sw);
          setTimeout(() => sw.remove(), 650);
        }, 320);
    }
    if (!f.replaced) floater(`${vsj.infoName(item)} broke!`, 'var(--danger)', bagCard);
  });

  const flyIcon = (item: string, count: number) => {
    sfx.playEvent('items_gained', 0.96 + Math.random() * 0.09);
    if (ui.reducedMotion()) {
      flashClass(bagCard, 'pop');
      return;
    }
    const fightUp = !!vsj.at('fight');
    const srcEl = fightUp ? views.location.root : apanel.root;
    const from = srcEl.getBoundingClientRect();
    const to = bagCard.getBoundingClientRect();
    const node = el('div', { style: 'position:fixed;z-index:90;pointer-events:none;width:32px;height:32px' });
    node.append(icons.texture(item, 32));
    if (count > 1)
      node.append(el('span', { class: 'k-small', style: 'position:absolute;right:-6px;bottom:-4px;text-shadow:0 1px 2px #000', text: `×${count}` }));
    document.body.append(node);
    const x0 = from.left + from.width / 2, y0 = from.top + from.height / 2;
    const x1 = to.left + to.width / 2, y1 = to.top + to.height / 2;
    const apexX = x0 + (x1 - x0) * 0.72, apexY = y0 + (y1 - y0) * 0.72 - 34;
    node.animate(
      [
        { transform: `translate(${x0}px, ${y0}px) scale(0.6)`, opacity: 0 },
        { transform: `translate(${x0}px, ${y0}px) scale(1.35)`, opacity: 1, offset: 0.15 },
        { transform: `translate(${apexX}px, ${apexY}px) scale(1.1)`, opacity: 1, offset: 0.72 },
        { transform: `translate(${x1}px, ${y1}px) scale(0.5)`, opacity: 0.3 },
      ],
      { duration: 650, easing: 'ease-in-out' },
    ).onfinish = () => {
      node.remove();
      flashClass(bagCard, 'pop');
    };
  };

  // ---- one-shot callouts ----
  const calloutSeen = (id: string): boolean => localStorage.getItem('stoneheim.callout.' + id) != null;
  let calloutLive = false;
  const tryCallout = (id: string, text: string, target: HTMLElement) => {
    if (vsj.at('welcome')) return; // nothing coaches over the welcome popup
    if (calloutSeen(id) || calloutLive || !target.isConnected) return;
    calloutLive = true;
    localStorage.setItem('stoneheim.callout.' + id, '1');
    const r = target.getBoundingClientRect();
    const bubble = el('div', {
      class: 'callout',
      style: `left:${Math.min(r.left, innerWidth - 260)}px;top:${Math.min(r.bottom + 6, innerHeight - 80)}px`,
      text,
    });
    document.body.append(bubble);
    const dismiss = () => {
      bubble.remove();
      calloutLive = false;
      document.removeEventListener('pointerdown', dismiss);
    };
    setTimeout(() => document.addEventListener('pointerdown', dismiss), 100);
  };
  let hadFight = false;
  const tryStateCallouts = () => {
    tryCallout('quest_bar_intro', "This is your quest bar — see it at the top of the screen whenever you're lost.", qbar.root);
    if ((vsj.at('guidance.glow') ?? []).length) tryCallout('glow_intro', 'The orange glow marks your next step.', rail);
    if (vsj.at('bench')) tryCallout('bench_intro', 'The bench: pick materials, then Start.', views.crafting.root);
  };
  ui.onSelect((id) => {
    if (id) tryCallout('info_pane_intro', 'Anything you click is explained here.', ipane.root);
  });
  fx.onFxType('items_gained', () => tryCallout('bag_intro', 'Loot lands in your bag here.', bagCard));

  // ---- initial paint ----
  refreshTabs();
  refreshCombatTab();
  refreshBag();
  refreshPools();
  refreshModals();
  showFirstVisibleTab();
  tryStateCallouts();

  return {
    root,
    onVsj(changed) {
      const all = changed.has('*');
      if (all || changed.has('tabs') || changed.has('reveal') || changed.has('guidance')) refreshTabs();
      if (all || changed.has('bags') || changed.has('infos')) refreshBag();
      if (all || changed.has('character')) refreshPools();
      if (all || changed.has('welcome') || changed.has('offline_report') || changed.has('quest_bar')) refreshModals();
      if (all || changed.has('fight')) {
        refreshCombatTab();
        const has = !!vsj.at('fight');
        if (has && !hadFight)
          tryCallout('fight_intro', 'A fight! Your side is on the left — watch the swing bars, eat to heal, or Flee.', tabButtons['location']);
        hadFight = has;
      }
      if (all || changed.has('welcome') || changed.has('guidance') || changed.has('bench')) tryStateCallouts();
      qbar.refresh(changed);
      apanel.refresh(changed);
      ipane.refresh(changed);
      for (const id of Object.keys(views)) views[id].refresh(changed);
    },
  };
}
